import { z } from 'zod';

// ---------------------------------------------------------------------------
// Transaction DTO — mirrors backend §17 response shape
// ---------------------------------------------------------------------------

export const TransactionDtoSchema = z.object({
  transaction_id: z.string(),
  id: z.number().optional(),
  client_id: z.number(),
  transaction_type: z.enum(['payment', 'fx_trade', 'incoming']),
  status: z.string(),
  amount: z.string(),
  currency: z.string(),
  reference_currency: z.string().optional(),
  rate: z.string().optional(),
  fee_applied: z.string().optional(),
  sender_name: z.string().optional(),
  beneficiary_name: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  description: z.string().optional(),
  raw_json: z.record(z.unknown()).optional(),
});

export type TransactionDto = z.infer<typeof TransactionDtoSchema>;

// ---------------------------------------------------------------------------
// Paginated API response
// ---------------------------------------------------------------------------

export const TransactionHistoryResponseSchema = z.object({
  transactions: z.array(TransactionDtoSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  filters_applied: z.object({
    type: z.string().optional(),
    status: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    searchQuery: z.string().optional(),
  }),
});

export type TransactionHistoryResponse = z.infer<typeof TransactionHistoryResponseSchema>;

export const WebappRawTransactionSchema = z
  .object({
    type: z.enum(['payment', 'fx_trade', 'incoming']),
    payment_id: z.union([z.number(), z.string()]).optional(),
    fx_trade_id: z.union([z.number(), z.string()]).optional(),
    incoming_payment_id: z.union([z.number(), z.string()]).optional(),
    transaction_id: z.string().nullable().optional(),
    client_id: z.union([z.number(), z.string()]).optional(),
    client: z.union([z.number(), z.string()]).optional(),
    status: z.string().optional(),
    payment_status: z.union([z.number(), z.string()]).optional(),
    fx_trade_status: z.union([z.number(), z.string()]).optional(),
    incoming_payment_status: z.union([z.number(), z.string()]).optional(),
    amount: z.union([z.string(), z.number()]).nullable().optional(),
    currency: z.string().nullable().optional(),
    currency_code: z.string().nullable().optional(),
    sell_currency: z.string().nullable().optional(),
    buy_currency: z.string().nullable().optional(),
    rate: z.union([z.string(), z.number()]).nullable().optional(),
    fee_applied: z.union([z.string(), z.number()]).nullable().optional(),
    sender_name: z.string().nullable().optional(),
    beneficiary_name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export type WebappRawTransaction = z.infer<typeof WebappRawTransactionSchema>;

export const WebappTransactionsResponseSchema = z.object({
  data: z.array(WebappRawTransactionSchema),
  total: z.number(),
});

export type WebappTransactionsResponse = z.infer<typeof WebappTransactionsResponseSchema>;

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

export interface HistoryFilters {
  type: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  searchQuery: string;
}

export const DEFAULT_HISTORY_FILTERS: HistoryFilters = {
  type: 'all',
  status: 'all',
  dateFrom: '',
  dateTo: '',
  searchQuery: '',
};

// ---------------------------------------------------------------------------
// Display mappings
// ---------------------------------------------------------------------------

export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  payment: 'Payment',
  fx_trade: 'FX Trade',
  incoming: 'Incoming',
};

export const TRANSACTION_TYPE_BADGE_VARIANT: Record<string, 'info' | 'warning' | 'success'> = {
  payment: 'info',
  fx_trade: 'warning',
  incoming: 'success',
};

export const STATUS_BADGE_VARIANT: Record<
  string,
  'warning' | 'info' | 'success' | 'error' | 'default'
> = {
  pending: 'warning',
  approved: 'info',
  processing: 'info',
  completed: 'success',
  executed: 'success',
  credited: 'success',
  rejected: 'error',
  cancelled: 'default',
  matched: 'info',
};
